import { TeamInvitation, TeamMember } from '@anm/api/modules/team';
import InvitationsList from '@anm/components/team/InvitationsList';
import { InvitationItemInputProps } from '@anm/components/team/InvitationsList/InvitationItem';
import { RoleChange } from '@anm/components/team/InvitationsList/TeamRolesModal';
import useSwitchState from '@anm/hooks/useSwitchState';
import { useCallback, useEffect, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { teamActions, teamSelectors } from '../../../store/modules/team';
import { userSelectors } from '../../../store/modules/user';
import InviteMember from '../InviteMember';

export type InvitationsListSectionProps = {};

const mapInviteDataToViewItem = (list: TeamInvitation[]): InvitationItemInputProps[] =>
  list.map(({ userId, userName, teamRole, inviteKey, userEmail, state }) => ({
    userId,
    userName,
    inviteKey,
    email: userEmail,
    isTeamMember: false,
    inviteStatus: state,
    role: teamRole
  }));
const mapMemberToViewItem = (list: TeamMember[]): InvitationItemInputProps[] =>
  list.map(({ userId, email, avatar, displayName, role }) => ({
    role,
    userId,
    email,
    avatar,
    isTeamMember: true,
    userName: displayName
  }));

const InvitationsListSection: FC<InvitationsListSectionProps> = () => {
  const [isInviteMemberOpen, openInviteMember, closeInviteMember] = useSwitchState();

  const dispatch = useDispatch();
  const limit = useSelector(userSelectors.selectMaxTeamUsers);
  const currentUser = useSelector(userSelectors.selectUserProfile);
  const userPermissions = useSelector(userSelectors.selectTeamPermissions);

  const inviteList = useSelector(teamSelectors.selectInvitationsList);
  const teamMembers = useSelector(teamSelectors.selectTeamMembers) || [];
  const filteredInviteList = inviteList.filter(invite => !teamMembers?.some(team => invite.userId === team.userId));
  const list = [...mapMemberToViewItem(teamMembers), ...mapInviteDataToViewItem(filteredInviteList)];

  const isDeleteUserPending = useSelector(teamSelectors.selectDeleteMemberPending);
  const isDeleteInvitePending = useSelector(teamSelectors.selectDeleteInvitePending);
  const isRoleChangePending = useSelector(teamSelectors.selectUpdateRolePending);

  const handleDeleteMember = useCallback((memberId: string) => {
    dispatch(teamActions.deleteMember({ memberId }));
  }, []);

  const handleDeleteInvite = useCallback((hash: string) => {
    dispatch(teamActions.deleteInvite({ hash }));
  }, []);

  const handleRoleChange = useCallback(({ userId, role }: RoleChange) => {
    dispatch(teamActions.updateMemberRole({ memberId: userId, role }));
  }, []);
  const handleResendInvite = useCallback((hash: string) => {
    dispatch(teamActions.resendInvite(hash));
  }, []);

  useEffect(() => {
    if (inviteList.length > 1) return;
    dispatch(teamActions.getInvitationsList());
  }, []);

  if (!limit || !list.length) return null;

  return (
    <>
      {isInviteMemberOpen && <InviteMember onModalClose={closeInviteMember} />}
      <InvitationsList
        currentUserId={currentUser?.userId}
        list={list}
        limit={limit}
        canUpgrade={limit < 10}
        permissions={userPermissions}
        isDeletePending={isDeleteUserPending || isDeleteInvitePending}
        isChangeRolePending={isRoleChangePending}
        onMemberDelete={handleDeleteMember}
        onInviteDelete={handleDeleteInvite}
        onRoleChange={handleRoleChange}
        onInviteMember={openInviteMember}
        onInviteResend={handleResendInvite}
      />
    </>
  );
};

export default InvitationsListSection;
